if (!window.customElements.get('choose-offer-form')) {
  const WINBACKS_SELECTED_OFFER_STORAGE_KEY = 'winbacks:selected-offer';
  const WINBACKS_SELECTED_DISCOUNT_STORAGE_KEY = 'winbacks:selected-reference';
  const WINBACKS_PARENT_SUBSCRIPTION_STORAGE_KEY = 'winbacks:parent-subscription';

  class ChooseOfferForm extends HTMLElement {
    constructor() {
      super();
      this.offerTiles = this.querySelectorAll('[data-offer-tile]');
      this.form = this.querySelector('#newsletter-form');
      this.emailInput = this.querySelector('input[name="contact[email]"]');
      this.builderUrl = this.dataset.builderUrl;
      this.discount = this.querySelector('[data-reference-input]')?.value;
      this.isWinbacksFlow = this.dataset.winbacksFlow === 'true';
      this.submitButton = this.querySelector('[data-cyo-submit]');
    }
    connectedCallback() {
      const listContainer = this.querySelector('[role="list"]');
      if (listContainer) {
        listContainer.setAttribute('role', 'radiogroup');
        listContainer.setAttribute('aria-label', 'Choose an offer');
      }

      if (this.submitButton) {
        this.submitButton.addEventListener('click', () => this.handleRemoveStoredDiscount());
      }
      this.offerTiles.forEach((tile) => {
        const isSelected = tile.classList.contains('selected');
        tile.setAttribute('tabindex', isSelected ? '0' : '-1');

        tile.addEventListener('click', (e) => this.handleOfferSelect(e.currentTarget));
        tile.addEventListener('keydown', (e) => this.handleKeydown(e));
      });

      if (this.isWinbacksFlow) {
        this.persistSelectedOffer();
        if (this.discount) {
          this.persistDiscount(this.discount);
        }
        this.registerSubmitHandlers();

        this.preloadParentSubscription();
      } else {
        this.handleRemoveStoredDiscount();
      }

      if (this.form && this.emailInput) {
        this.form.addEventListener('submit', () => {
          window.localStorage.setItem('chooseOfferEmail', this.emailInput.value);
        });
      }
    }

    handleRemoveStoredDiscount() {
      window.localStorage.removeItem('subscriptionBuilderDiscount');
    }

    handleOfferSelect(tile) {
      this.offerTiles.forEach((t) => {
        t.classList.remove('selected');
        t.setAttribute('aria-checked', 'false');
        t.setAttribute('tabindex', '-1');
        const radio = t.querySelector('[data-offer-radio]');
        if (radio) radio.checked = false;
      });

      tile.classList.add('selected');
      tile.setAttribute('aria-checked', 'true');
      tile.setAttribute('tabindex', '0');

      const radio = tile.querySelector('[data-offer-radio]');

      if (radio) radio.checked = true;

      const variantId = tile.dataset.variantId;
      const productId = tile.dataset.offerProductId;
      const hasValidIds = Boolean(variantId && productId);

      if (this.form) {
        const returnToInput = this.form?.querySelector('input[name="return_to"]');
        if (returnToInput && hasValidIds) {
          returnToInput.value = `${this.builderUrl}?variant-id=${variantId}&product-id=${productId}&reference=${this.discount || ''}${this.hasAttribute('data-go-to-step-2') ? '&step2Redirect=true' : ''}`;
        }
      } else {
        const submitButton = this.querySelector('a[data-submit]');
        let refValue = `${this.builderUrl}`;
        if (submitButton) {
          if (hasValidIds) {
            refValue = `${refValue}?variant-id=${variantId}&product-id=${productId}`;
            if (this.discount) {
              refValue = `${refValue}&reference=${this.discount}`;
            }
            submitButton.href = refValue;
          } else {
            submitButton.setAttribute('aria-disabled', 'true');
            submitButton.classList.add('disabled');
          }
        }
      }

      this.persistSelectedOffer(tile);
    }

    handleKeydown(e) {
      const tile = e.currentTarget;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleOfferSelect(tile);
        return;
      }

      const isArrowKey = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key);
      if (!isArrowKey) return;

      e.preventDefault();

      const tiles = Array.from(this.offerTiles);
      const currentIndex = tiles.indexOf(tile);

      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % tiles.length;
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + tiles.length) % tiles.length;
      }

      const nextTile = tiles[nextIndex];
      if (!nextTile) return;

      nextTile.focus();
    }

    registerSubmitHandlers() {
      this.addEventListener('submit', () => {
        this.persistSelectedOffer();
      });

      const submitButton = this.querySelector('[data-submit]');
      if (submitButton) {
        submitButton.addEventListener('click', () => {
          this.persistSelectedOffer();
        });
      }
    }

    getSelectedOfferTile() {
      return this.querySelector('[data-offer-tile].selected') || this.offerTiles[0] || null;
    }

    persistDiscount(discount) {
      try {
        localStorage.setItem(WINBACKS_SELECTED_DISCOUNT_STORAGE_KEY, discount);
      } catch {
        // localStorage can be unavailable in private mode or restricted browsers.
      }
    }

    persistSelectedOffer(tile = null) {
      const selectedTile = tile || this.getSelectedOfferTile();
      if (!selectedTile) return;

      const variantId = selectedTile.dataset.variantId ? String(selectedTile.dataset.variantId) : null;
      const productId = selectedTile.dataset.offerProductId ? String(selectedTile.dataset.offerProductId) : null;
      if (!variantId || !productId) return;

      try {
        localStorage.setItem(
          WINBACKS_SELECTED_OFFER_STORAGE_KEY,
          JSON.stringify({
            variantId,
            productId,
            savedAt: new Date().toISOString(),
          })
        );
      } catch {
        // localStorage can be unavailable in private mode or restricted browsers.
      }
    }

    async waitForRechargeApi() {
      const maxAttempts = 20;
      let attempts = 0;

      while (!window.ButcherBoxRechargeProductCardApi && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts += 1;
      }

      return window.ButcherBoxRechargeProductCardApi || null;
    }

    async preloadParentSubscription() {
      try {
        const rechargeApi = await this.waitForRechargeApi();
        if (!rechargeApi?.getWinbackParentSubscription) return;

        const parentSubscription = await rechargeApi.getWinbackParentSubscription();
        if (!parentSubscription) return;

        localStorage.setItem(
          WINBACKS_PARENT_SUBSCRIPTION_STORAGE_KEY,
          JSON.stringify({
            ...parentSubscription,
            savedAt: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.warn('Unable to preload winbacks parent subscription.', error);
      }
    }
  }

  window.customElements.define('choose-offer-form', ChooseOfferForm);
}
